/**
 * MobileWeeklyBillsPlanner.jsx
 * 
 * A mobile-optimized React component for weekly financial planning and bill management.
 * This component provides a user-friendly interface for tracking bills, revenue, and expenses
 * with responsive design specifically tailored for mobile devices.
 * 
 * Key Features:
 * - Weekly financial planning with date-based navigation
 * - Bill management (add, edit, delete, skip bills)
 * - Purchase tracking with daily breakdown
 * - Revenue tracking with customizable profit and tax rates
 * - Automatic calculations for daily and weekly totals
 * - Persistent storage using localStorage
 * - Special handling for specific days (Sunday: no revenue, Friday: payroll, Tuesday: Virtustant)
 * 
 * State Management:
 * - weekDates: Array of dates for the current week
 * - bills: Array of bill objects with properties (name, dueDay, amount, frequency)
 * - dailyRevenue: Object mapping dates to revenue amounts
 * - purchases: Nested object structure for tracking purchases by week and date
 * - customProfitTax: Object for storing custom profit/tax rates per date
 * - weekHistory: Object storing historical data for each week
 * 
 * Key Calculations:
 * - Daily balance forward
 * - Profit calculations (default 10% or custom)
 * - Tax calculations (default 10% or custom)
 * - Weekly totals for revenue, bills, and purchases
 * 
 * Data Persistence:
 * - Bills data
 * - Purchase history
 * - Weekly history
 * - Custom profit/tax settings
 * - Skipped bills
 * 
 * Mobile-Specific Features:
 * - Collapsible day cards
 * - Touch-friendly interface
 * - Responsive design
 * - Mobile-optimized dialogs
 * - Bottom navigation
 * 
 * @component
 * @requires React
 * @requires useState
 * @requires useEffect
 * @requires useCallback
 */

import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase/config';
import { signOut } from 'firebase/auth';
import { useWeekData } from '../hooks/useWeekData';
import { useGlobalData } from '../hooks/useGlobalData';
import DebtSnowball from './DebtSnowball';
import '../styles/MobileWeeklyBillsPlanner.css';

const MobileWeeklyBillsPlanner = ({ userId, workspaceId }) => {

  // Local UI states (week navigation)
  const [currentWeekId, setCurrentWeekId] = useState('');

  // Get global data (shared across weeks)
  const {
    loading: globalLoading,
    error: globalError,
    bills,
    skippedBills,
    updateBills,
    updateSkippedBills
  } = useGlobalData(workspaceId);

  // Get current week data (completely isolated)
  const {
    loading: weekLoading,
    error: weekError,
    isSaving,
    lastSaved,
    weekDates,
    dailyRevenue,
    weeklyPayroll,
    startingBalance,
    purchases,
    customProfitTax,
    initializeWeek,
    saveWeekData,
    updateDailyRevenue,
    updateWeeklyPayroll,
    updateStartingBalance,
    addPurchase,
    removePurchase,
    updateCustomProfitTaxForDate,
    setWeekDates,
    setDailyRevenue,
    setPurchases,
    setCustomProfitTax
  } = useWeekData(workspaceId, currentWeekId);



  // UI-only local states (data states now come from hooks)
  const [startDate, setStartDate] = useState('');
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [csvUploaded, setCsvUploaded] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [showAddBillDialog, setShowAddBillDialog] = useState(false);
  const [newBill, setNewBill] = useState({ name: '', dueDay: 1, amount: '', frequency: 'Monthly' });
  const [showAddPurchaseDialog, setShowAddPurchaseDialog] = useState(false);
  const [newPurchase, setNewPurchase] = useState({ name: '', amount: '', date: '' });
  const [showBillsManager, setShowBillsManager] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [editingBillData, setEditingBillData] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [activeDay, setActiveDay] = useState(null);
  const [editingBill, setEditingBill] = useState(null);
  const [editingBillOriginalAmount, setEditingBillOriginalAmount] = useState(null);
  const [moveBillInfo, setMoveBillInfo] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [showDebtSnowball, setShowDebtSnowball] = useState(false);
  const [billSearchTerm, setBillSearchTerm] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [pendingWeekSwitch, setPendingWeekSwitch] = useState(null);
  
  // Manual save button state is handled by isSaving from hook

  // Combined loading and error states
  const loading = globalLoading || weekLoading;
  const error = globalError || weekError;
  


  // Generate week ID from start date (YYYY-MM-DD format)
  const getWeekId = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Manual save function
  const handleManualSave = async () => {
    if (!currentWeekId || weekDates.length === 0) return;
    try {
      await saveWeekData();
      setHasUnsavedChanges(false); // Clear unsaved changes flag after successful save
      console.log('Week data saved successfully');
    } catch (error) {
      console.error('Error saving week data:', error);
      alert('Error saving week data. Please try again.');
    }
  };

  // Mark data as having unsaved changes
  const markAsUnsaved = () => {
    setHasUnsavedChanges(true);
  };

  // Clear unsaved changes flag (when loading new week)
  const markAsSaved = () => {
    setHasUnsavedChanges(false);
  };

  // Get bills for a specific date
  const getBillsForDate = (date) => {
    if (!bills || bills.length === 0) return [];
    
    const dateStr = formatDateString(date);
    const dayOfMonth = date.getDate();
    const dayOfWeek = date.getDay();
    
    // Check if this bill is skipped for this date
    const isSkipped = (billId) => {
      return skippedBills?.[dateStr]?.includes(billId) || false;
    };
    
    return bills.filter(bill => {
      if (isSkipped(bill.id)) return false;
      
      // Handle special cases
      if (bill.name.toLowerCase().includes('virtustant') && dayOfWeek === 2) return true;
      if (bill.rawDueDay === 'Tuesday' && dayOfWeek === 2) return true;
      
      // Handle frequency-based bills
      switch (bill.frequency) {
        case 'Weekly':
          return dayOfWeek === parseInt(bill.dueDay);
        case 'Biweekly':
          const weekNumber = Math.floor(date.getTime() / (1000 * 60 * 60 * 24 * 7));
          return dayOfWeek === parseInt(bill.dueDay) && weekNumber % 2 === 0;
        case 'Every30Days':
          const startDate = new Date(bill.dueDay);
          const diffTime = date.getTime() - startDate.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          return diffDays >= 0 && diffDays % 30 === 0;
        case 'Monthly':
        default:
          return dayOfMonth === parseInt(bill.dueDay);
      }
    });
  };

  // Get purchases for a specific date
  const getPurchasesForDate = (date) => {
    const dateStr = formatDateString(date);
    return purchases?.[dateStr] || [];
  };

  // Calculate balance forward for a specific day index
  const calculateBalanceForward = (dayIndex) => {
    if (dayIndex === 0) return startingBalance;
    
    let balance = startingBalance;
    
    for (let i = 0; i < dayIndex; i++) {
      const date = weekDates[i];
      const newBalanceForward = calculateNewBalanceForward(date, calculateIntermediateBalance(date, balance));
      balance = newBalanceForward;
    }
    
    return balance;
  };

  // Calculate intermediate balance (after bills and purchases)
  const calculateIntermediateBalance = (date, balanceForward) => {
    const dateBills = getBillsForDate(date);
    const billsTotal = dateBills.reduce((sum, bill) => sum + bill.amount, 0);
    
    const datePurchases = getPurchasesForDate(date);
    const purchasesTotal = datePurchases.reduce((sum, purchase) => sum + purchase.amount, 0);
    
    const dayOfWeek = date.getDay();
    const isFriday = dayOfWeek === 5;
    const payrollDeduction = isFriday ? (parseFloat(weeklyPayroll) || 0) : 0;
    
    return balanceForward - payrollDeduction - billsTotal - purchasesTotal;
  };

  // Calculate profit for a specific date
  const calculateProfitForDate = (date) => {
    const dateStr = formatDateString(date);
    const revenue = dailyRevenue[dateStr] || 0;
    const customSettings = customProfitTax?.[dateStr];
    
    if (customSettings?.useCustomProfit) {
      return (revenue * (customSettings.profitAmount / 100));
    }
    return revenue * 0.1; // Default 10%
  };

  // Calculate tax for a specific date
  const calculateTaxForDate = (date) => {
    const dateStr = formatDateString(date);
    const revenue = dailyRevenue[dateStr] || 0;
    const customSettings = customProfitTax?.[dateStr];
    
    if (customSettings?.useCustomTax) {
      return (revenue * (customSettings.taxAmount / 100));
    }
    return revenue * 0.1; // Default 10%
  };

  // Calculate new balance forward
  const calculateNewBalanceForward = (date, intermediateBalance) => {
    const dateStr = formatDateString(date);
    const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
    
    if (dayName === 'Sunday') return intermediateBalance;
    
    const revenue = dailyRevenue[dateStr] || 0;
    const profit = calculateProfitForDate(date);
    const taxes = calculateTaxForDate(date);
    
    return intermediateBalance + revenue - profit - taxes;
  };

  // Format currency
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return `$${numAmount.toFixed(2)}`;
  };

  // Format date to YYYY-MM-DD without timezone conversion
  const formatDateString = (date) => {
    if (typeof date === 'string') {
      // If it's already a string, assume it's in the correct format
      return date;
    }
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter bills based on search term
  const getFilteredBills = () => {
    if (!bills) return [];
    if (!billSearchTerm.trim()) return bills;
    
    const searchLower = billSearchTerm.toLowerCase().trim();
    return bills.filter(bill => 
      bill.name.toLowerCase().includes(searchLower) ||
      bill.amount.toString().includes(searchLower) ||
      bill.frequency.toLowerCase().includes(searchLower)
    );
  };

  // Calculate total revenue for the week
  const calculateTotalRevenue = () => {
    return Object.values(dailyRevenue).reduce((sum, revenue) => sum + revenue, 0);
  };

  // Calculate total bills for the week
  const calculateTotalBills = () => {
    let total = 0;
    weekDates.forEach(date => {
      const dateBills = getBillsForDate(date);
      total += dateBills.reduce((sum, bill) => sum + bill.amount, 0);
    });
    return total;
  };

  // Calculate total purchases for the week
  const calculateTotalPurchases = () => {
    let total = 0;
    weekDates.forEach(date => {
      const datePurchases = getPurchasesForDate(date);
      total += datePurchases.reduce((sum, purchase) => sum + purchase.amount, 0);
    });
    return total;
  };

  // Calculate total profit for the week
  const calculateTotalProfit = () => {
    if (!weekDates.length) return 0;
    
    let totalProfit = 0;
    weekDates.forEach(date => {
      totalProfit += calculateProfitForDate(date);
    });
    
    return totalProfit;
  };

  // Calculate total taxes for the week
  const calculateTotalTaxes = () => {
    if (!weekDates.length) return 0;
    
    let totalTaxes = 0;
    weekDates.forEach(date => {
      totalTaxes += calculateTaxForDate(date);
    });
    
    return totalTaxes;
  };

  // Calculate final balance forward (end of week)
  const calculateFinalBalanceForward = () => {
    if (!weekDates.length) return 0;
    
    // Get the last day of the week (Saturday)
    const lastDay = weekDates[weekDates.length - 1];
    const lastDayIndex = weekDates.length - 1;
    
    // Calculate intermediate balance for the last day
    const balanceForward = calculateBalanceForward(lastDayIndex);
    const intermediateBalance = calculateIntermediateBalance(lastDay, balanceForward);
    
    // Return the new balance forward for the last day
    return calculateNewBalanceForward(lastDay, intermediateBalance);
  };

  // Format date range for display
  const formatDateRange = () => {
    if (weekDates.length === 0) return '';
    
    const startDate = weekDates[0];
    const endDate = weekDates[weekDates.length - 1];
    
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${startDate.toLocaleDateString(undefined, options)} - ${endDate.toLocaleDateString(undefined, options)}`;
  };

  // Find the Sunday (start of week) for any given date
  const findSundayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const sunday = new Date(date);
    sunday.setDate(date.getDate() - dayOfWeek);
    return sunday.toISOString().split('T')[0];
  };



  // Safe week switching with save prompts
  const switchToWeek = useCallback(async (startDateStr) => {
    const sundayDateStr = findSundayOfWeek(startDateStr);
    const weekId = getWeekId(sundayDateStr);
    
    console.log('switchToWeek called:', { 
      startDateStr, 
      sundayDateStr, 
      weekId, 
      hasUnsavedChanges, 
      currentWeekId 
    });
    
    // If we have unsaved changes and data to save, prompt the user
    if (hasUnsavedChanges && currentWeekId && weekDates.length > 0) {
      console.log('Showing save prompt due to unsaved changes');
      setPendingWeekSwitch(weekId);
      setShowSavePrompt(true);
      return; // Stop here and wait for user decision
    }
    
    // No unsaved changes or no current data, proceed with switch
    try {
      // If we have current week data, save it before switching
      if (currentWeekId && weekDates.length > 0) {
        console.log('Auto-saving current week before switch');
        await saveWeekData();
      }
      
      console.log('Proceeding with week switch to:', weekId);
      setCurrentWeekId(weekId);
      markAsSaved(); // New week starts with no unsaved changes
    } catch (error) {
      console.error('Error during week switch:', error);
      // Continue with switch even if save fails to prevent getting stuck
      setCurrentWeekId(weekId);
      markAsSaved();
    }
  }, [hasUnsavedChanges, currentWeekId, weekDates.length, saveWeekData]);

  // Handle save prompt responses
  const handleSavePromptSave = async () => {
    try {
      await handleManualSave();
      setShowSavePrompt(false);
      if (pendingWeekSwitch) {
        setCurrentWeekId(pendingWeekSwitch);
        setPendingWeekSwitch(null);
        markAsSaved(); // New week starts with no unsaved changes
      }
    } catch (error) {
      console.error('Error saving before week switch:', error);
    }
  };

  const handleSavePromptDiscard = () => {
    setShowSavePrompt(false);
    if (pendingWeekSwitch) {
      setCurrentWeekId(pendingWeekSwitch);
      setPendingWeekSwitch(null);
      markAsSaved(); // New week starts with no unsaved changes
    }
    setHasUnsavedChanges(false); // Clear unsaved changes flag
  };

  const handleSavePromptCancel = () => {
    setShowSavePrompt(false);
    setPendingWeekSwitch(null);
  };

  // Generate week dates starting from the selected date - now uses safe switching
  const generateWeekDatesFromStart = useCallback((startDateStr) => {
    switchToWeek(startDateStr);
    setShowDateDialog(false);
  }, [switchToWeek]);

  // Handle setting start date from dialog - simplified with new hooks
  const handleSetWeekDates = async () => {
    if (!startDate) return;
    
    // Switch to new week - new hook will handle loading/initialization
    generateWeekDatesFromStart(startDate);
  };

  // Handle switching between weeks - now uses safe switching
  const handleSwitchToWeek = async (weekId) => {
    // Use the safe switching mechanism
    await switchToWeek(weekId);
  };

  // Navigate to previous week
  const goToPreviousWeek = async () => {
    if (!currentWeekId) return;
    
    // Parse current week ID and subtract 7 days
    const [year, month, day] = currentWeekId.split('-').map(Number);
    const currentSunday = new Date(year, month - 1, day);
    const previousSunday = new Date(currentSunday);
    previousSunday.setDate(currentSunday.getDate() - 7);
    
    const prevYear = previousSunday.getFullYear();
    const prevMonth = String(previousSunday.getMonth() + 1).padStart(2, '0');
    const prevDay = String(previousSunday.getDate()).padStart(2, '0');
    const previousWeekDateStr = `${prevYear}-${prevMonth}-${prevDay}`;
    
    await switchToWeek(previousWeekDateStr);
  };

  // Navigate to next week
  const goToNextWeek = async () => {
    if (!currentWeekId) return;
    
    // Parse current week ID and add 7 days
    const [year, month, day] = currentWeekId.split('-').map(Number);
    const currentSunday = new Date(year, month - 1, day);
    const nextSunday = new Date(currentSunday);
    nextSunday.setDate(currentSunday.getDate() + 7);
    
    const nextYear = nextSunday.getFullYear();
    const nextMonth = String(nextSunday.getMonth() + 1).padStart(2, '0');
    const nextDay = String(nextSunday.getDate()).padStart(2, '0');
    const nextWeekDateStr = `${nextYear}-${nextMonth}-${nextDay}`;
    
    await switchToWeek(nextWeekDateStr);
  };

  // Handle starting balance change - use hook function
  const handleStartingBalanceChange = async (value) => {
    updateStartingBalance(value);
    markAsUnsaved();
  };

  // Handle daily revenue change - use hook function
  const handleDailyRevenueChange = (dateStr, value) => {
    updateDailyRevenue(dateStr, value);
    markAsUnsaved();
  };

  // Handle custom profit/tax changes - use hook function
  const toggleCustomProfit = async (dateStr) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, useCustomProfit: !current.useCustomProfit };
    updateCustomProfitTaxForDate(dateStr, updated);
    markAsUnsaved();
  };

  const toggleCustomTax = async (dateStr) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, useCustomTax: !current.useCustomTax };
    updateCustomProfitTaxForDate(dateStr, updated);
    markAsUnsaved();
  };

  const handleCustomProfitChange = async (dateStr, value) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: true, profitAmount: 10, useCustomTax: false, taxAmount: 10 };
    const updated = { ...current, profitAmount: parseFloat(value) || 0 };
    updateCustomProfitTaxForDate(dateStr, updated);
    markAsUnsaved();
  };

  const handleCustomTaxChange = async (dateStr, value) => {
    const current = customProfitTax[dateStr] || { useCustomProfit: false, profitAmount: 10, useCustomTax: true, taxAmount: 10 };
    const updated = { ...current, taxAmount: parseFloat(value) || 0 };
    updateCustomProfitTaxForDate(dateStr, updated);
    markAsUnsaved();
  };

  // Handle bill editing
  const handleEditBillAmount = async (billId, newAmount) => {
    const updatedBills = bills.map(bill => 
      bill.id === billId ? { ...bill, amount: parseFloat(newAmount) || 0 } : bill
    );
    await updateBills(updatedBills);
  };

  const handleCancelEdit = async () => {
    if (editingBill && editingBillOriginalAmount !== null) {
      const updatedBills = bills.map(bill => 
        bill.id === editingBill ? { ...bill, amount: editingBillOriginalAmount } : bill
      );
      await updateBills(updatedBills);
    }
    setEditingBill(null);
    setEditingBillOriginalAmount(null);
  };

  // Handle bill moving
  const handleMoveBill = async (billId, newDay) => {
    const updatedBills = bills.map(bill => 
      bill.id === billId ? { ...bill, dueDay: parseInt(newDay) } : bill
    );
    await updateBills(updatedBills);
    setMoveBillInfo(null);
  };



    // Set CSV uploaded status based on bills
  useEffect(() => {
    if (bills && bills.length > 0) {
      setCsvUploaded(true);
    }
  }, [bills]);

  // Initialize with current week when component loads
  useEffect(() => {
    if (!globalLoading && !currentWeekId) {
      console.log('Initializing current week...');
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      // Get Sunday of current week
      const date = new Date(todayStr);
      const dayOfWeek = date.getDay();
      const sunday = new Date(date);
      sunday.setDate(date.getDate() - dayOfWeek);
      const sundayDateStr = sunday.toISOString().split('T')[0];
      
      // Set current week ID
      setCurrentWeekId(sundayDateStr);
      console.log('Set week ID to:', sundayDateStr);
    }
  }, [globalLoading, currentWeekId]);

  // Initialize week data when currentWeekId is set but weekDates is empty
  useEffect(() => {
    if (currentWeekId && weekDates.length === 0 && !weekLoading) {
      console.log('Initializing week data for:', currentWeekId);
      initializeWeek(currentWeekId);
    }
  }, [currentWeekId, weekDates.length, weekLoading, initializeWeek]);

  // Clear unsaved changes when a new week is loaded
  useEffect(() => {
    if (weekDates.length > 0) {
      // New week has been loaded, clear unsaved changes flag
      markAsSaved();
    }
  }, [currentWeekId, weekDates.length]);

  // Safety net: Clear unsaved changes if they persist too long without real changes
  useEffect(() => {
    if (hasUnsavedChanges && weekDates.length > 0) {
      const timeout = setTimeout(() => {
        // If no real changes happened in 5 seconds, clear the flag
        console.log('Auto-clearing stuck unsaved changes flag');
        setHasUnsavedChanges(false);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [hasUnsavedChanges, weekDates.length]);



  // No cleanup needed for manual save



  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        const content = e.target.result;
        const lines = content.split('\n');
        const headers = lines[0].split(',');
        
        const nameIndex = headers.findIndex(h => h.trim() === 'Name');
        const dueDayIndex = headers.findIndex(h => h.trim() === 'Due Day');
        const amountIndex = headers.findIndex(h => h.trim() === 'Amount');
        const frequencyIndex = headers.findIndex(h => h.trim() === 'Frequency');
        
        const parsedBills = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          let values = [];
          let inQuote = false;
          let currentValue = '';
          
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            
            if (char === '"') {
              inQuote = !inQuote;
            } else if (char === ',' && !inQuote) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          
          values.push(currentValue.trim());
          
          if (values.length < Math.max(nameIndex, dueDayIndex, amountIndex, frequencyIndex) + 1) {
            continue;
          }
          
          const name = values[nameIndex]?.trim();
          const dueDayRaw = values[dueDayIndex]?.trim();
          const dueDay = parseInt(dueDayRaw);
          
          let amount = values[amountIndex]?.trim() || '0';
          amount = amount.replace(/^\$|\s+/g, '');
          amount = parseFloat(amount.replace(/,/g, ''));
          
          const frequency = values[frequencyIndex]?.trim();
          
          if (name && !isNaN(amount)) {
            parsedBills.push({
              id: Date.now() + i,
              name,
              dueDay: isNaN(dueDay) ? 1 : dueDay,
              amount,
              frequency,
              rawDueDay: dueDayRaw
            });
          }
        }
        
        await updateBills(parsedBills);
        setCsvUploaded(true);
      };
      
      reader.readAsText(file);
    }
  };

  // Handle adding a bill
  const handleAddBill = async () => {
    if (newBill.name && newBill.amount && newBill.dueDay) {
      const updatedBills = [...(bills || [])];
      
      // Determine the appropriate rawDueDay based on frequency
      let rawDueDay = newBill.dueDay;
      if (newBill.frequency === 'Weekly' || newBill.frequency === 'Biweekly') {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        rawDueDay = dayNames[parseInt(newBill.dueDay)];
      }
      
      updatedBills.push({
        id: Date.now(),
        name: newBill.name,
        dueDay: newBill.frequency === 'Every30Days' ? newBill.dueDay : parseInt(newBill.dueDay),
        amount: parseFloat(newBill.amount) || 0,
        frequency: newBill.frequency,
        rawDueDay: rawDueDay
      });
      
      await updateBills(updatedBills);
      setNewBill({ name: '', dueDay: '', amount: '', frequency: 'Monthly' });
      setShowAddBillDialog(false);
    }
  };

  // Handle removing a bill
  const handleRemoveBill = async (billId) => {
    const updatedBills = bills.filter(bill => bill.id !== billId);
    await updateBills(updatedBills);
  };

  // Handle skipping a bill
  const handleSkipBill = async (date, billId) => {
    const dateStr = formatDateString(date);
    const updatedSkippedBills = { ...skippedBills };
    
    if (!updatedSkippedBills[dateStr]) {
      updatedSkippedBills[dateStr] = [];
    }
    
    updatedSkippedBills[dateStr].push(billId);
    await updateSkippedBills(updatedSkippedBills);
  };

  // Handle adding a purchase - use hook function
  const handleAddPurchase = async () => {
    if (newPurchase.name && newPurchase.amount && newPurchase.date) {
      const dateStr = newPurchase.date; // This is already in YYYY-MM-DD format from date input
      
      const isInCurrentWeek = weekDates.some(date => 
        formatDateString(date) === dateStr
      );
      
      if (!isInCurrentWeek) {
        alert('Please select a date within the current week.');
        return;
      }
      
      const purchase = {
        id: Date.now(),
        name: newPurchase.name,
        amount: parseFloat(newPurchase.amount) || 0
      };
      
      addPurchase(dateStr, purchase);
      markAsUnsaved();
      setNewPurchase({ name: '', amount: '', date: '' });
      setShowAddPurchaseDialog(false);
    }
  };

  // Handle removing a purchase - use hook function
  const handleRemovePurchase = async (date, purchaseId) => {
    const dateStr = formatDateString(date);
    removePurchase(dateStr, purchaseId);
    markAsUnsaved();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle dark mode toggle
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', JSON.stringify(newDarkMode));
  };

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
  }, [darkMode]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h1>Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="mobile-weekly-bills-planner">
      {/* Mobile Header */}
      <div className="mobile-header">
        <button 
          className="menu-button"
          onClick={() => setShowMenu(!showMenu)}
        >
          ☰
        </button>
        <div className="header-title">
          <h1>Weekly Bills</h1>
          {showDebug && (
            <small style={{color: hasUnsavedChanges ? 'orange' : 'green'}}>
              {hasUnsavedChanges ? 'Unsaved Changes' : 'All Saved'}
            </small>
          )}
        </div>
        <div className="header-actions">
          <button 
            className={`action-button ${hasUnsavedChanges ? 'unsaved-changes' : ''}`}
            onClick={handleManualSave}
            disabled={isSaving || !hasUnsavedChanges}
            title={hasUnsavedChanges ? 'Save Changes (Unsaved)' : 'No Changes to Save'}
          >
            {isSaving ? '💾' : hasUnsavedChanges ? '💾*' : '💾'}
          </button>
          <button 
            className="action-button"
            onClick={() => setShowBillsManager(true)}
            title="Search & Manage Bills"
          >
            🔍
          </button>
          <button 
            className="action-button"
            onClick={toggleDarkMode}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>
        <button 
          className="action-button"
          onClick={() => setShowDateDialog(true)}
        >
          📅
        </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMenu && (
        <div className="mobile-menu">
          <div className="menu-overlay" onClick={() => setShowMenu(false)} />
          <div className="menu-content">
            <button onClick={() => {
              setShowAddBillDialog(true);
              setShowMenu(false);
            }}>
              Add Bill
            </button>
            <button onClick={() => {
              setShowAddPurchaseDialog(true);
              setShowMenu(false);
            }}>
              Add Purchase
            </button>
            <div className="file-upload">
              <label>
                {csvUploaded ? 'Update Bills CSV' : 'Upload Bills CSV'}
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    handleFileUpload(e);
                    setShowMenu(false);
                  }}
                />
              </label>
            </div>
            <div className="payroll-input">
              <label>Weekly Payroll:</label>
              <div className="input-with-symbol">
                <span>$</span>
                <input
                  type="number"
                  value={weeklyPayroll}
                  onChange={(e) => {
                    updateWeeklyPayroll(e.target.value);
                    markAsUnsaved();
                  }}
                  placeholder="Enter amount"
                />
              </div>
            </div>
            <button onClick={() => {
              setShowDebug(!showDebug);
              setShowMenu(false);
            }}>
              {showDebug ? 'Hide Debug' : 'Debug Bills'}
            </button>
            {showDebug && (
              <>
                <button onClick={() => {
                  setHasUnsavedChanges(false);
                  setShowMenu(false);
                  console.log('Force cleared unsaved changes flag');
                }}>
                  🔧 Force Clear Unsaved
                </button>
                <button onClick={() => {
                  setShowSavePrompt(false);
                  setPendingWeekSwitch(null);
                  setShowMenu(false);
                  console.log('Force cleared save prompt');
                }}>
                  🔧 Clear Save Prompt
                </button>
              </>
            )}
            <button onClick={() => {
              setShowBillsManager(true);
              setShowMenu(false);
            }}>
              🔍 Search & Manage Bills
            </button>
            <button onClick={() => {
              setShowDebtSnowball(true);
              setShowMenu(false);
            }}>
              💳 Debt Snowball
            </button>
            <button 
              onClick={() => {
                toggleDarkMode();
                setShowMenu(false);
              }}
              className="theme-toggle-button"
            >
              {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
            </button>
            <button 
              onClick={handleLogout}
              className="logout-button"
            >
              Logout
            </button>
          </div>
        </div>
      )}

      {/* Add Bill Dialog */}
      {showAddBillDialog && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Add New Bill</h3>
            <input
              type="text"
              placeholder="Bill Name"
              value={newBill.name}
              onChange={(e) => setNewBill({ ...newBill, name: e.target.value })}
            />
            
            <select
              value={newBill.frequency}
              onChange={(e) => setNewBill({ ...newBill, frequency: e.target.value, dueDay: '' })}
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Biweekly">Biweekly</option>
              <option value="Every30Days">Every 30 Days</option>
            </select>
            
            {(newBill.frequency === 'Weekly' || newBill.frequency === 'Biweekly') && (
              <select
                value={newBill.dueDay}
                onChange={(e) => setNewBill({ ...newBill, dueDay: e.target.value })}
              >
                <option value="">Select Day of Week</option>
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </select>
            )}
            
            {newBill.frequency === 'Monthly' && (
              <input
                type="number"
                placeholder="Due Day (1-31)"
                value={newBill.dueDay}
                onChange={(e) => setNewBill({ ...newBill, dueDay: e.target.value })}
                min="1"
                max="31"
              />
            )}
            
            {newBill.frequency === 'Every30Days' && (
              <input
                type="date"
                placeholder="Start Date"
                value={newBill.dueDay}
                onChange={(e) => setNewBill({ ...newBill, dueDay: e.target.value })}
              />
            )}
            
            <input
              type="number"
              placeholder="Amount"
              value={newBill.amount}
              onChange={(e) => setNewBill({ ...newBill, amount: e.target.value })}
              step="0.01"
            />
            <div className="dialog-buttons">
              <button onClick={() => setShowAddBillDialog(false)}>Cancel</button>
              <button 
                onClick={handleAddBill}
                disabled={!newBill.name || !newBill.amount || !newBill.dueDay}
              >
                Add Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Purchase Dialog */}
      {showAddPurchaseDialog && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Add New Purchase</h3>
            <input
              type="text"
              placeholder="Purchase Name"
              value={newPurchase.name}
              onChange={(e) => setNewPurchase({ ...newPurchase, name: e.target.value })}
            />
            <input
              type="number"
              placeholder="Amount"
              value={newPurchase.amount}
              onChange={(e) => setNewPurchase({ ...newPurchase, amount: e.target.value })}
              step="0.01"
            />
            <input
              type="date"
              value={newPurchase.date}
              onChange={(e) => setNewPurchase({ ...newPurchase, date: e.target.value })}
            />
            <div className="dialog-buttons">
              <button onClick={() => setShowAddPurchaseDialog(false)}>Cancel</button>
              <button 
                onClick={handleAddPurchase}
                disabled={!newPurchase.name || !newPurchase.amount || !newPurchase.date}
              >
                Add Purchase
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Selection Dialog */}
      {showDateDialog && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Select Week Start Date</h3>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <div className="dialog-buttons">
              <button onClick={() => setShowDateDialog(false)}>Cancel</button>
              <button 
                onClick={handleSetWeekDates}
                disabled={!startDate}
              >
                Set Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Prompt Dialog */}
      {showSavePrompt && (
        <div className="mobile-dialog" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div className="dialog-content save-prompt" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            maxWidth: '90%',
            width: '400px',
            textAlign: 'center',
            border: '3px solid #ff6b6b'
          }}>
            <h3 style={{ color: '#ff6b6b', marginBottom: '15px', fontSize: '18px' }}>⚠️ Unsaved Changes Detected</h3>
            <p style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.4' }}>
              You have unsaved changes in the current week. To navigate to a different week, please choose one of the options below:
            </p>
            <div className="dialog-buttons" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <button 
                onClick={handleSavePromptSave}
                className="save-button"
                disabled={isSaving}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving ? 'Saving...' : '💾 Save Changes & Navigate'}
              </button>
              <button 
                onClick={handleSavePromptDiscard}
                className="discard-button"
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                🗑️ Discard Changes & Navigate
              </button>
              <button 
                onClick={handleSavePromptCancel}
                style={{
                  backgroundColor: '#9e9e9e',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ❌ Cancel Navigation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bills Manager Dialog */}
      {showBillsManager && (
        <div className="mobile-dialog">
          <div className="dialog-content bills-manager">
            <div className="bills-manager-header">
              <h3>Manage Bills</h3>
              <button onClick={() => {
                setShowBillsManager(false);
                setBillSearchTerm(''); // Clear search when closing
              }}>&times;</button>
            </div>
            
            {/* Search Input */}
            <div className="bills-search">
              <input
                type="text"
                placeholder="Search bills by name, amount, or frequency..."
                value={billSearchTerm}
                onChange={(e) => setBillSearchTerm(e.target.value)}
                className="search-input"
              />
              {billSearchTerm && (
                <button 
                  onClick={() => setBillSearchTerm('')}
                  className="clear-search"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Bills List */}
            <div className="bills-list">
              {getFilteredBills().length === 0 ? (
                <div className="no-bills-message">
                  {billSearchTerm ? 'No bills found matching your search.' : 'No bills found.'}
                </div>
              ) : (
                getFilteredBills().map(bill => (
                  <div key={bill.id} className="bill-item">
                    <div className="bill-details">
                      <div className="bill-name">{bill.name}</div>
                      <div className="bill-info">
                        <span className="bill-amount">${bill.amount}</span>
                        <span className="bill-frequency">{bill.frequency}</span>
                      </div>
                    </div>
                    <div className="bill-actions">
                      <button onClick={() => {
                        setEditingBillId(bill.id);
                        // Ensure proper formatting for editing
                        const formattedBill = {
                          ...bill,
                          amount: parseFloat(bill.amount) || 0,
                          dueDay: bill.frequency === 'Weekly' || bill.frequency === 'Biweekly' ? 
                            (typeof bill.dueDay === 'string' ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(bill.rawDueDay || bill.dueDay) : bill.dueDay) :
                            bill.dueDay
                        };
                        setEditingBillData(formattedBill);
                      }}>Edit</button>
                      <button onClick={() => handleRemoveBill(bill.id)}>Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Results Count */}
            {bills && bills.length > 0 && (
              <div className="bills-count">
                {billSearchTerm ? 
                  `Showing ${getFilteredBills().length} of ${bills.length} bills` :
                  `${bills.length} bills total`
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Bill Dialog */}
      {editingBillId && (
        <div className="mobile-dialog">
          <div className="dialog-content">
            <h3>Edit Bill</h3>
            <input
              type="text"
              placeholder="Bill Name"
              value={editingBillData.name}
              onChange={(e) => setEditingBillData({ ...editingBillData, name: e.target.value })}
            />
            
            <select
              value={editingBillData.frequency}
              onChange={(e) => setEditingBillData({ ...editingBillData, frequency: e.target.value, dueDay: '' })}
            >
              <option value="Monthly">Monthly</option>
              <option value="Weekly">Weekly</option>
              <option value="Biweekly">Biweekly</option>
              <option value="Every30Days">Every 30 Days</option>
            </select>
            
            {(editingBillData.frequency === 'Weekly' || editingBillData.frequency === 'Biweekly') && (
              <select
                value={editingBillData.dueDay}
                onChange={(e) => setEditingBillData({ ...editingBillData, dueDay: e.target.value })}
              >
                <option value="">Select Day of Week</option>
                <option value="0">Sunday</option>
                <option value="1">Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
              </select>
            )}
            
            {editingBillData.frequency === 'Monthly' && (
              <input
                type="number"
                placeholder="Due Day (1-31)"
                value={editingBillData.dueDay}
                onChange={(e) => setEditingBillData({ ...editingBillData, dueDay: e.target.value })}
                min="1"
                max="31"
              />
            )}
            
            {editingBillData.frequency === 'Every30Days' && (
              <input
                type="date"
                placeholder="Start Date"
                value={editingBillData.dueDay}
                onChange={(e) => setEditingBillData({ ...editingBillData, dueDay: e.target.value })}
              />
            )}
            
            <input
              type="number"
              placeholder="Amount"
              value={editingBillData.amount}
              onChange={(e) => setEditingBillData({ ...editingBillData, amount: e.target.value })}
              step="0.01"
            />
            <div className="dialog-buttons">
              <button onClick={() => {
                setEditingBillId(null);
                setEditingBillData(null);
              }}>Cancel</button>
              <button 
                onClick={async () => {
                  try {
                    // Ensure proper data formatting before saving
                    const formattedBillData = {
                      ...editingBillData,
                      amount: parseFloat(editingBillData.amount) || 0,
                      dueDay: editingBillData.frequency === 'Every30Days' ? editingBillData.dueDay : parseInt(editingBillData.dueDay)
                    };
                    
                    // Update rawDueDay for weekly bills
                    if (formattedBillData.frequency === 'Weekly' || formattedBillData.frequency === 'Biweekly') {
                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                      formattedBillData.rawDueDay = dayNames[parseInt(formattedBillData.dueDay)];
                    }
                    
                    const updatedBills = bills.map(bill => 
                      bill.id === editingBillId ? formattedBillData : bill
                    );
                    await updateBills(updatedBills);
                    setEditingBillId(null);
                    setEditingBillData(null);
                  } catch (error) {
                    console.error('Error updating bill:', error);
                    alert('Error saving bill. Please try again.');
                  }
                }}
                disabled={!editingBillData.name || !editingBillData.amount || !editingBillData.dueDay}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Week Summary */}
      {weekDates.length > 0 && (
        <div className="mobile-week-summary">
          <div className="week-header">
            <button 
              className="week-nav-button"
              onClick={goToPreviousWeek}
              title="Previous Week"
            >
              ◀
            </button>
            <h2>{formatDateRange()}</h2>
            <button 
              className="week-nav-button"
              onClick={goToNextWeek}
              title="Next Week"
            >
              ▶
            </button>
          </div>
          
          <div className="revenue-total">
            <h3>Revenue Total: {formatCurrency(calculateTotalRevenue())}</h3>
          </div>
          
          <div className="summary-grid">
            <div className="summary-item">
              <span className="label">Profit</span>
              <span className="value">{formatCurrency(calculateTotalProfit())}</span>
            </div>
            <div className="summary-item">
              <span className="label">Taxes</span>
              <span className="value">{formatCurrency(calculateTotalTaxes())}</span>
            </div>
            <div className="summary-item">
              <span className="label">Payroll</span>
              <span className="value">{formatCurrency(parseFloat(weeklyPayroll) || 0)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Bills</span>
              <span className="value">{formatCurrency(calculateTotalBills())}</span>
            </div>
            <div className="summary-item">
              <span className="label">Purchases</span>
              <span className="value">{formatCurrency(calculateTotalPurchases())}</span>
            </div>
            <div className="summary-item final">
              <span className="label">Balance Forward</span>
              <span className="value">{formatCurrency(calculateFinalBalanceForward())}</span>
            </div>
          </div>
        </div>
      )}

      {/* Daily Breakdown */}
      {weekDates.length > 0 && (
        <div className="mobile-daily-breakdown">
          {weekDates.map((date, index) => {
            const dateStr = formatDateString(date);
            const dayName = date.toLocaleDateString(undefined, { weekday: 'long' });
            const formattedDate = date.toLocaleDateString();
            const isSunday = dayName === 'Sunday';
            const isFriday = dayName === 'Friday';
            const isTuesday = dayName === 'Tuesday';
            
            // Get bills for this date
            const dateBills = getBillsForDate(date);
            const billsTotal = dateBills.reduce((sum, bill) => sum + bill.amount, 0);
            
            // Get purchases for this date
            const datePurchases = getPurchasesForDate(date);
            const purchasesTotal = datePurchases.reduce((sum, purchase) => sum + purchase.amount, 0);
            
            // Get balance calculations
            const balanceForward = calculateBalanceForward(index);
            const intermediateBalance = calculateIntermediateBalance(date, balanceForward);
            const newBalanceForward = calculateNewBalanceForward(date, intermediateBalance);
            
            // Get revenue, profit, taxes
            const revenue = isSunday ? 0 : (dailyRevenue[dateStr] || 0);
            const profit = calculateProfitForDate(date);
            const taxes = calculateTaxForDate(date);
            
            // Get custom settings for this date
            const customSettings = customProfitTax?.[dateStr] || { 
              useCustomProfit: false, 
              profitAmount: 10, 
              useCustomTax: false, 
              taxAmount: 10 
            };
            
            // Handle Friday payroll calculation
            const payrollDeduction = (isFriday) ? (parseFloat(weeklyPayroll) || 0) : 0;
            const balanceAfterPayroll = isFriday ? (balanceForward - payrollDeduction) : balanceForward;
            
            const isActive = activeDay === index;
            
            return (
              <div className="mobile-day-card" key={index}>
                <div 
                  className="mobile-day-header"
                  onClick={() => setActiveDay(isActive ? null : index)}
                >
                  <div className="day-info">
                    <h3>{dayName}</h3>
                    <span>{formattedDate}</span>
                  </div>
                  <div className="day-summary">
                    <span className="balance">
                      {formatCurrency(newBalanceForward)}
                    </span>
                    <span className="expand-icon">
                      {isActive ? '▼' : '▶'}
                    </span>
                  </div>
                </div>
                
                {isActive && (
                  <div className="mobile-day-content">
                    {isSunday && (
                      <div className="mobile-day-section">
                        <h4>Starting Balance</h4>
                        <input
                          type="number"
                          value={startingBalance || ''}
                          onChange={(e) => handleStartingBalanceChange(e.target.value)}
                          placeholder="0.00"
                          className="mobile-input"
                        />
                      </div>
                    )}
                    
                    {/* Special handling for Friday and Tuesday */}
                    {(isFriday || isTuesday) && (
                      <div className="mobile-day-section special-calculation">
                        <h4>{isFriday ? 'Payroll Calculation' : 'Virtustant Calculation'}</h4>
                        <div className="calculation-breakdown">
                          <div className="calc-row">
                            <span>Balance Forward:</span>
                            <span>{formatCurrency(balanceForward)}</span>
                          </div>
                          
                          {isFriday && (
                            <div className="calc-row">
                              <span>Payroll Deduction:</span>
                              <span>- {formatCurrency(payrollDeduction)}</span>
                            </div>
                          )}
                          
                          <div className="calc-divider"></div>
                          <div className="calc-row total">
                            <span>Remaining Balance:</span>
                            <span>{formatCurrency(balanceAfterPayroll)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Balance Calculation Section */}
                    <div className="mobile-day-section">
                      <h4>Balance Calculation</h4>
                      
                      <div className="calc-row">
                        <span>Balance Forward:</span>
                        <span>{formatCurrency((isFriday || isTuesday) ? balanceAfterPayroll : balanceForward)}</span>
                      </div>
                      
                      {/* Bills Section */}
                      <div className="subsection">
                        <div className="subsection-header">
                          <span>Bills ({dateBills.length})</span>
                          <span>{formatCurrency(billsTotal)}</span>
                        </div>
                        {dateBills.length > 0 && (
                          <div className="bills-list">
                            {dateBills.map((bill, billIndex) => (
                              <div key={billIndex} className="bill-item">
                                <div className="bill-info">
                                  <span className="bill-name">{bill.name}</span>
                                  {editingBill === bill.id ? (
                                    <div className="bill-edit">
                                      <input
                                        type="number"
                                        value={bill.amount}
                                        onChange={(e) => handleEditBillAmount(bill.id, e.target.value)}
                                        step="0.01"
                                        className="mobile-input small"
                                      />
                                      <button onClick={() => setEditingBill(null)}>✓</button>
                                      <button onClick={handleCancelEdit}>✕</button>
                                    </div>
                                  ) : (
                                    <span 
                                      onClick={() => {
                                        setEditingBillOriginalAmount(bill.amount);
                                        setEditingBill(bill.id);
                                      }} 
                                      className="bill-amount"
                                    >
                                      {formatCurrency(bill.amount)}
                                    </span>
                                  )}
                                </div>
                                <div className="bill-actions">
                                  <button onClick={() => setMoveBillInfo({ id: bill.id, currentDay: bill.dueDay })}>
                                    Move
                                  </button>
                                  <button onClick={() => handleSkipBill(date, bill.id)}>
                                    Skip
                                  </button>
                                </div>
                                
                                {moveBillInfo && moveBillInfo.id === bill.id && (
                                  <div className="move-bill-dialog">
                                    <select
                                      value={moveBillInfo.newDay || bill.dueDay}
                                      onChange={(e) => setMoveBillInfo({...moveBillInfo, newDay: e.target.value})}
                                    >
                                      {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                                        <option key={day} value={day}>Day {day}</option>
                                      ))}
                                    </select>
                                    <button 
                                      onClick={() => handleMoveBill(bill.id, moveBillInfo.newDay || bill.dueDay)}
                                    >
                                      Move
                                    </button>
                                    <button onClick={() => setMoveBillInfo(null)}>Cancel</button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Purchases Section */}
                      <div className="subsection">
                        <div className="subsection-header">
                          <span>Purchases ({datePurchases.length})</span>
                          <span>{formatCurrency(purchasesTotal)}</span>
                        </div>
                        {datePurchases.length > 0 && (
                          <div className="purchases-list">
                            {datePurchases.map((purchase, purchaseIndex) => (
                              <div key={purchaseIndex} className="purchase-item">
                                <div className="purchase-info">
                                  <span className="purchase-name">{purchase.name}</span>
                                  <span className="purchase-amount">{formatCurrency(purchase.amount)}</span>
                                </div>
                                <button 
                                  onClick={() => handleRemovePurchase(date, purchase.id)} 
                                  className="remove-button"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setNewPurchase({ ...newPurchase, date: dateStr });
                            setShowAddPurchaseDialog(true);
                          }}
                          className="add-button"
                        >
                          + Add Purchase
                        </button>
                      </div>
                      
                      <div className="calc-summary">
                        <div className="calc-row">
                          <span>Balance Forward:</span>
                          <span>{formatCurrency((isFriday || isTuesday) ? balanceAfterPayroll : balanceForward)}</span>
                        </div>
                        <div className="calc-row">
                          <span>Total Bills:</span>
                          <span>- {formatCurrency(billsTotal)}</span>
                        </div>
                        <div className="calc-row">
                          <span>Total Purchases:</span>
                          <span>- {formatCurrency(purchasesTotal)}</span>
                        </div>
                        <div className="calc-divider"></div>
                        <div className="calc-row total">
                          <span>Subtotal:</span>
                          <span>{formatCurrency(intermediateBalance)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Revenue Section */}
                    <div className="mobile-day-section">
                      <h4>Revenue Calculation</h4>
                      
                      <div className="revenue-input">
                        <label>Revenue:</label>
                        {!isSunday ? (
                          <input
                            type="number"
                            value={dailyRevenue[dateStr] || ''}
                            onChange={(e) => handleDailyRevenueChange(dateStr, e.target.value)}
                            placeholder="0.00"
                            className="mobile-input"
                          />
                        ) : (
                          <span>{formatCurrency(0)}</span>
                        )}
                      </div>
                      
                      {!isSunday && (
                        <div className="profit-tax-controls">
                          <div className="control-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={!customSettings.useCustomProfit}
                                onChange={() => toggleCustomProfit(dateStr)}
                              />
                              Use Default Profit (10%)
                            </label>
                            
                            {customSettings.useCustomProfit && (
                              <div className="custom-input">
                                <label>Custom Profit (%):</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={customSettings.profitAmount}
                                  onChange={(e) => handleCustomProfitChange(dateStr, e.target.value)}
                                  className="mobile-input small"
                                />
                              </div>
                            )}
                          </div>
                          
                          <div className="control-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={!customSettings.useCustomTax}
                                onChange={() => toggleCustomTax(dateStr)}
                              />
                              Use Default Tax (10%)
                            </label>
                            
                            {customSettings.useCustomTax && (
                              <div className="custom-input">
                                <label>Custom Tax (%):</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={customSettings.taxAmount}
                                  onChange={(e) => handleCustomTaxChange(dateStr, e.target.value)}
                                  className="mobile-input small"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="calc-summary">
                        <div className="calc-row">
                          <span>Revenue:</span>
                          <span>{formatCurrency(revenue)}</span>
                        </div>
                        <div className="calc-row">
                          <span>Profit ({customSettings.useCustomProfit ? `${customSettings.profitAmount}%` : '10%'}):</span>
                          <span>- {formatCurrency(profit)}</span>
                        </div>
                        <div className="calc-row">
                          <span>Taxes ({customSettings.useCustomTax ? `${customSettings.taxAmount}%` : '10%'}):</span>
                          <span>- {formatCurrency(taxes)}</span>
                        </div>
                        <div className="calc-divider"></div>
                        <div className="calc-row total">
                          <span>Net Revenue:</span>
                          <span>{formatCurrency(revenue - profit - taxes)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* New Balance Forward */}
                    <div className="mobile-day-section final-balance">
                      <div className="calc-row total large">
                        <span>New Balance Forward:</span>
                        <span>{formatCurrency(newBalanceForward)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Print Button */}
      {weekDates.length > 0 && (
        <div className="mobile-print-section">
          <button 
            onClick={() => window.print()}
            className="print-button"
          >
            Print Planner
          </button>
        </div>
      )}

      {/* Debug Section */}
      {showDebug && (
        <div className="debug-section">
          <h3>Debug Information</h3>
          <div className="debug-info">
            <h4>Current Week:</h4>
            <p>Week ID: {currentWeekId}</p>
            <p>Dates: {weekDates.length} days loaded</p>
            {isSaving && <p style={{color: 'orange'}}>💾 Saving...</p>}
            {lastSaved && !isSaving && <p style={{color: 'green'}}>✅ Last saved: {lastSaved.toLocaleTimeString()}</p>}
          </div>
          <pre>{JSON.stringify({ 
            currentWeekId,
            weekDates: weekDates.length,
            hasUnsavedChanges,
            showSavePrompt,
            pendingWeekSwitch,
            loadingState: { globalLoading, weekLoading, isSaving },
            currentWeekData: {
              dailyRevenue,
              weeklyPayroll,
              startingBalance,
              purchases,
              hasSkippedBills: Object.keys(skippedBills || {}).filter(date => 
                weekDates.some(d => d.toISOString().split('T')[0] === date)
              ).length > 0,
              hasCustomProfitTax: Object.keys(customProfitTax || {}).filter(date => 
                weekDates.some(d => d.toISOString().split('T')[0] === date)
              ).length > 0
            }
          }, null, 2)}</pre>
        </div>
      )}

      {/* Debt Snowball */}
      {showDebtSnowball && (
        <DebtSnowball 
          workspaceId={workspaceId}
          onClose={() => setShowDebtSnowball(false)}
        />
      )}
    </div>
  );
};

export default MobileWeeklyBillsPlanner; 